import { create } from 'zustand';
import { workoutSessionApi } from '@/services/workoutSessionApi';
import { SessionProgressResponse } from '@/types/workoutSession';
import { WorkoutExerciseSetDTO } from '@/types/workout';

interface SessionStore {
  sessionProgress: SessionProgressResponse | null;
  sessionId: string | null;
  isActive: boolean;
  loading: boolean;
  error: string | null;
  isSyncing: boolean;

  // Actions
  startSession: (workoutId: string) => Promise<void>;
  pauseSession: () => Promise<void>;
  resumeSession: () => Promise<void>;
  completeSet: (
    setId: string,
    data?: {
      actual_reps?: number;
      actual_weight?: number;
      actual_duration_seconds?: number;
      actual_rpe?: number;
    }
  ) => Promise<void>;
  completeSession: () => Promise<void>;
  getCurrentSet: () => Promise<WorkoutExerciseSetDTO | null>;
  updateSetMetadata: (
    setId: string,
    metadata: {
      reps?: number;
      duration_seconds?: number;
      weight?: number;
      rpe?: number;
    }
  ) => Promise<void>;
  syncProgress: () => Promise<void>;
  clearSession: () => void;
}

export const useSessionStore = create<SessionStore>((set, get) => ({
  sessionProgress: null,
  sessionId: null,
  isActive: false,
  loading: false,
  error: null,
  isSyncing: false,

  startSession: async (workoutId: string) => {
    set({ loading: true, error: null });
    try {
      // Check for existing draft session first, or create new one
      const response = await workoutSessionApi.getOrCreateDraftSession(workoutId);

      set({
        sessionId: response.session_id,
        isActive: true,
        loading: false,
      });

      // Fetch initial progress
      await get().syncProgress();
    } catch (error) {
      console.error('Failed to start session:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to start session',
        loading: false,
      });
      throw error;
    }
  },

  pauseSession: async () => {
    const { sessionId } = get();
    if (!sessionId) return;

    set({ loading: true, error: null });
    try {
      await workoutSessionApi.pauseSession(sessionId);
      await get().syncProgress();
      set({ isActive: false, loading: false });
    } catch (error) {
      console.error('Failed to pause session:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to pause session',
        loading: false,
      });
    }
  },

  resumeSession: async () => {
    const { sessionId } = get();
    if (!sessionId) return;

    set({ loading: true, error: null });
    try {
      await workoutSessionApi.resumeSession(sessionId);
      await get().syncProgress();
      set({ isActive: true, loading: false });
    } catch (error) {
      console.error('Failed to resume session:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to resume session',
        loading: false,
      });
    }
  },

  completeSet: async (
    setId: string,
    data = {}
  ) => {
    const { sessionId } = get();
    if (!sessionId) return;

    set({ loading: true, error: null });
    try {
      // Start the set if not already started
      await workoutSessionApi.startSet(sessionId, setId);

      // Update metadata if provided
      if (data.actual_reps || data.actual_weight || data.actual_rpe || data.actual_duration_seconds) {
        await workoutSessionApi.updateSetMetadata(sessionId, setId, {
          reps: data.actual_reps,
          weight: data.actual_weight,
          rpe: data.actual_rpe,
          duration_seconds: data.actual_duration_seconds,
        });
      }

      // Complete the set
      await workoutSessionApi.completeSet(sessionId, setId);

      // Sync progress after completing set to get next set
      await get().syncProgress();
      set({ loading: false });
    } catch (error) {
      console.error('Failed to complete set:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to complete set',
        loading: false,
      });
    }
  },

  getCurrentSet: async () => {
    const { sessionId } = get();
    if (!sessionId) return null;

    try {
      const progress = await workoutSessionApi.getProgress(sessionId);
      return progress.current_set || null;
    } catch (error) {
      console.error('Failed to get current set:', error);
      return null;
    }
  },

  updateSetMetadata: async (
    setId: string,
    metadata: {
      reps?: number;
      duration_seconds?: number;
      weight?: number;
      rpe?: number;
    }
  ) => {
    const { sessionId } = get();
    if (!sessionId) return;

    set({ loading: true, error: null });
    try {
      await workoutSessionApi.updateSetMetadata(sessionId, setId, metadata);
      // Refresh progress to get updated values
      await get().syncProgress();
      set({ loading: false });
    } catch (error) {
      console.error('Failed to update set metadata:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update set metadata',
        loading: false,
      });
    }
  },

  syncProgress: async () => {
    const { sessionId, isSyncing } = get();
    if (!sessionId || isSyncing) return; // Prevent concurrent syncs

    set({ isSyncing: true });
    try {
      const progress = await workoutSessionApi.getProgress(sessionId);
      set({ sessionProgress: progress });
      
      // Update isActive based on session status
      if (progress.session_status === 'completed') {
        set({ isActive: false });
      } else if (progress.session_status === 'paused') {
        set({ isActive: false });
      } else if (progress.session_status === 'in_progress') {
        set({ isActive: true });
      }
    } catch (error) {
      console.error('Failed to sync progress:', error);
      // Don't set error state for polling failures to avoid UI disruption
    } finally {
      set({ isSyncing: false });
    }
  },

  completeSession: async () => {
    const { sessionId } = get();
    if (!sessionId) return;

    set({ loading: true, error: null });
    try {
      await workoutSessionApi.completeSession(sessionId);
      await get().syncProgress();
      set({ isActive: false, loading: false });
    } catch (error) {
      console.error('Failed to complete session:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to complete session',
        loading: false,
      });
    }
  },

  clearSession: () => {
    set({ sessionProgress: null, sessionId: null, isActive: false, error: null, isSyncing: false });
  },
}));

